// src/app/jobs/jobs-search/jobs-search.component.ts
import {
    Component, computed, inject, signal, OnInit, DestroyRef,
    afterNextRender, Renderer2,
  } from '@angular/core';
  import { DecimalPipe, DOCUMENT } from '@angular/common';
  import { HttpClient, HttpParams } from '@angular/common/http';
  import { ActivatedRoute, Router, RouterLink } from '@angular/router';
  import { SeoService } from '@/app/core/seo/seo.service';
  import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
  import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
  import { catchError, of, skip, debounceTime, distinctUntilChanged } from 'rxjs';
  import { MatButtonModule } from '@angular/material/button';
  import { MatFormFieldModule } from '@angular/material/form-field';
  import { MatInputModule } from '@angular/material/input';
  import { MatSelectModule } from '@angular/material/select';
  import { MatSliderModule } from '@angular/material/slider';
  import { MatIconModule } from '@angular/material/icon';
  import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
  import { MatTooltipModule } from '@angular/material/tooltip';
  import { AdSlotComponent } from '../../ads/adslot/ad-slot.component';
  import { JobSearchCardComponent } from '../job-search-card/job-search-card.component';
import { JobPositionPublicDto, JobSearchResultPublicDto, MatchedLeadsProductPublicDto } from '@/app/models/public';
import { Media } from '@/app/core/media';
import {
  CdlClassFilterLookup,
  DriverTypeFilterLookup,
  EndorsementFilterLookup,
  ExperienceTypeFilterLookup,
  FreightTypeFilterLookup,
  HiringStateFilterLookup,
  JobFilterLookup,
} from '@/app/models/lookups/job-filters.lookup';
import { environment } from '@/environments/environment';
  
  const LIMIT = 24;
  
  interface LocationSuggestion {
    id: string;
    placeName: string;
    text: string;
    context: string;
    lat: number;
    lng: number;
  }
  
  @Component({
    selector: 'app-jobs-search',
    templateUrl: './jobs-search.component.html',
    imports: [
      RouterLink,
      ReactiveFormsModule,
      MatButtonModule,
      MatFormFieldModule,
      MatInputModule,
      MatSelectModule,
      MatSliderModule,
      MatIconModule,
      MatProgressSpinnerModule,
      MatTooltipModule,
      JobSearchCardComponent,
      AdSlotComponent,
      DecimalPipe,
    ],
  })
  export class JobsSearchComponent implements OnInit {
    private http       = inject(HttpClient);
    private route      = inject(ActivatedRoute);
    private router     = inject(Router);
    private seo        = inject(SeoService);
    private fb         = inject(FormBuilder);
    private renderer   = inject(Renderer2);
    private destroyRef = inject(DestroyRef);
    private document   = inject(DOCUMENT);
    private media = inject(Media);
  
    // ── Results state ──────────────────────────────────────────────────────
    jobs        = signal<JobPositionPublicDto[]>([]);
    totalJobs   = signal(0);
    currentPage = signal(1);
    loading     = signal(false);
    seed        = signal<number | null>(null);
  
    totalPages  = computed(() =>
      this.totalJobs() > 0 ? Math.ceil(this.totalJobs() / LIMIT) : 1
    );
  
    showingCount = computed(() => this.jobs().length);
  
    // ── Companies for dropdown ─────────────────────────────────────────────
    companies = signal<MatchedLeadsProductPublicDto[]>([]);
  
    // ── Filter panel (mobile collapse) ────────────────────────────────────
    filtersOpen = signal(true);
    protected isMobile = computed(() =>
      this.media.match(`(max-width: 480px)`)()
    );
  
    // ── Location state ─────────────────────────────────────────────────────
    locationLabel       = signal('');
    locationSuggestions = signal<LocationSuggestion[]>([]);
    locationStatus      = signal<'idle' | 'loading' | 'found' | 'error'>('idle');
    geoLocating         = signal(false);
    geoSet              = signal(false);
    hideSuggestions     = signal(true);
  
    private geoLat = signal<number | null>(null);
    private geoLng = signal<number | null>(null);
  
    radiusEnabled = computed(() =>
      !!this.filterForm.get('zip')?.value?.trim() ||
      (this.geoLat() !== null && this.geoLng() !== null)
    );
  
    // ── Filter form ────────────────────────────────────────────────────────
    filterForm: FormGroup = this.fb.group({
      q:              [''],
      hiringState:    [''],
      company:        [''],
      cdlClass:       [''],
      driverType:     [''],
      freightType:    [''],
      endorsement:    [''],
      experienceType: [''],
      zip:            [''],
      radius:         [50],
    });
  
    // ── Static option lists (from shared lookups) ──────────────────────────
    readonly cdlOptions = CdlClassFilterLookup.values;
    readonly driverTypeOptions = DriverTypeFilterLookup.values;
    readonly freightTypeOptions = FreightTypeFilterLookup.values;
    readonly endorsementOptions = EndorsementFilterLookup.values;
    readonly experienceOptions = ExperienceTypeFilterLookup.values;
    readonly stateOptions = HiringStateFilterLookup.values; 

    // ── Ads ────────────────────────────────────────────────────────────────
    readonly adSlotUrl = 'https://truckdriversus.com/ad-slot-companies-top';
    // ── Lifecycle ──────────────────────────────────────────────────────────
  
    ngOnInit(): void {
      this.seo.setPage({
        title: 'Search CDL Truck Driving Jobs',
        description: 'Search truck driving jobs near you by location, CDL class, driver type, freight type, '
          + 'endorsements, and experience on Truck Drivers USA.',
        path: '/jobs',
      });
  
      // Load companies for dropdown
      this.http.get<MatchedLeadsProductPublicDto[]>('/api/public/companies', {
        params: { sortBy: 'name' },
      }).pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(companies => this.companies.set(companies));
  
      // Seed from snapshot and load
      const snapshot = this.route.snapshot.queryParamMap;
      this.seedFormFromParams(snapshot);
      this.loadJobs();
  
      // Watch subsequent param changes (back/forward nav)
      this.route.queryParamMap
        .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(params => {
          this.seedFormFromParams(params);
          this.loadJobs();
        });
  
      // Watch zip field to toggle radius
      this.filterForm.get('zip')!.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(val => {
          if (val?.trim()) {
            this.geoLat.set(null);
            this.geoLng.set(null);
            this.geoSet.set(false);
            this.locationLabel.set('');
          }
        });
  
      // Mobile: start collapsed
      if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
        this.filtersOpen.set(false);
      }
    }
  
    constructor() {
      afterNextRender(() => {
        this.loadMapboxScript();
      });
    }
  
    // ── Param seeding ──────────────────────────────────────────────────────
  
    private seedFormFromParams(params: import('@angular/router').ParamMap): void {
      this.filterForm.patchValue({
        q:              params.get('q')              ?? '',
        hiringState:    this.optionKeyFromParam(HiringStateFilterLookup, params.get('hiringState')),
        company:        params.get('company')        ?? '',
        cdlClass:       this.optionKeyFromParam(CdlClassFilterLookup, params.get('cdlClass')),
        driverType:     this.optionKeyFromParam(DriverTypeFilterLookup, params.get('driverType')),
        freightType:    this.optionKeyFromParam(FreightTypeFilterLookup, params.get('freightType')),
        endorsement:    this.optionKeyFromParam(EndorsementFilterLookup, params.get('endorsement')),
        experienceType: this.optionKeyFromParam(ExperienceTypeFilterLookup, params.get('experienceType')),
        zip:            params.get('zip')            ?? '',
        radius:         params.get('radius')         ? Number(params.get('radius')) : 50,
      }, { emitEvent: false });
  
      this.currentPage.set(params.get('paged') ? Number(params.get('paged')) : 1);
      this.seed.set(params.get('seed') ? Number(params.get('seed')) : null);
      this.locationLabel.set(params.get('locationLabel') ?? '');
  
      const lat = params.get('lat');
      const lng = params.get('lng');
      if (lat && lng) {
        this.geoLat.set(Number(lat));
        this.geoLng.set(Number(lng));
        this.geoSet.set(true);
      }
    }

    /** Normalize URL values (`owner+operator` / `owner operator` → `owner-operator`) and accept legacy ids. */
    private optionKeyFromParam(
      Lookup: typeof JobFilterLookup,
      value: string | null,
    ): string {
      if (!value) return '';
      return Lookup.fromKey(value)?.key ?? Lookup.fromId(value)?.key ?? '';
    }

    private optionIdFromKey(
      Lookup: typeof JobFilterLookup,
      key: string | null | undefined,
    ): string | null {
      if (!key) return null;
      return Lookup.fromKey(key)?.id ?? null;
    }
  
    // ── Jobs fetch ─────────────────────────────────────────────────────────
  
    private loadJobs(): void {
      this.loading.set(true);
  
      const f      = this.filterForm.value;
      const offset = (this.currentPage() - 1) * LIMIT;
  
      let params = new HttpParams().set('limit', LIMIT).set('offset', offset);
  
      if (f.q) params = params.set('q', f.q);
      if (f.company) params = params.set('company', f.company);

      const hiringStateId = this.optionIdFromKey(HiringStateFilterLookup, f.hiringState);
      const cdlClassId = this.optionIdFromKey(CdlClassFilterLookup, f.cdlClass);
      const driverTypeId = this.optionIdFromKey(DriverTypeFilterLookup, f.driverType);
      const freightTypeId = this.optionIdFromKey(FreightTypeFilterLookup, f.freightType);
      const endorsementId = this.optionIdFromKey(EndorsementFilterLookup, f.endorsement);
      const experienceTypeId = this.optionIdFromKey(ExperienceTypeFilterLookup, f.experienceType);

      if (hiringStateId) params = params.set('hiringState', hiringStateId);
      if (cdlClassId) params = params.set('cdlClass', cdlClassId);
      if (driverTypeId) params = params.set('driverType', driverTypeId);
      if (freightTypeId) params = params.set('freightType', freightTypeId);
      if (endorsementId) params = params.set('endorsement', endorsementId);
      if (experienceTypeId) params = params.set('experienceType', experienceTypeId);

      if (f.zip)            params = params.set('zip',            f.zip);
      if (this.geoLat())    params = params.set('lat',            this.geoLat()!);
      if (this.geoLng())    params = params.set('lng',            this.geoLng()!);
  
      if ((f.zip || this.geoLat()) && f.radius > 0) {
        params = params.set('radius', f.radius);
      }
  
      if (this.seed()) params = params.set('seed', this.seed()!);
  
      this.http.get<JobSearchResultPublicDto>('/api/public/jobs', { params }).pipe(
        catchError(() => of({ results: [], total: 0, limit: LIMIT, offset: 0, seed: null })),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(result => {
        this.jobs.set(result.results ?? []);
        this.totalJobs.set(result.total ?? 0);
        if (result.seed && !this.seed()) {
          this.seed.set(result.seed);
        }
        this.loading.set(false);
      });
    }
  
    // ── Form actions ───────────────────────────────────────────────────────
  
    onSearch(): void {
      this.seed.set(null); // reset seed on new search
      this.currentPage.set(1);
      this.syncQueryParams();
      if (this.isMobile()) {
        this.filtersOpen.set(false);
      }
    }
  
    clearFilters(): void {
      this.filterForm.reset({
        q: '', hiringState: '', company: '', cdlClass: '',
        driverType: '', freightType: '', endorsement: '',
        experienceType: '', zip: '', radius: 50,
      });
      this.geoLat.set(null);
      this.geoLng.set(null);
      this.geoSet.set(false);
      this.locationLabel.set('');
      this.seed.set(null);
      this.currentPage.set(1);
      this.syncQueryParams();
    }
  
    goToPage(page: number): void {
      this.currentPage.set(page);
      this.syncQueryParams();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  
    private syncQueryParams(): void {
      const f = this.filterForm.value;
  
      this.router.navigate([], {
        relativeTo:          this.route,
        queryParams:         {
          q:              f.q              || null,
          hiringState:    f.hiringState    || null,
          company:        f.company        || null,
          cdlClass:       f.cdlClass       || null,
          driverType:     f.driverType     || null,
          freightType:    f.freightType    || null,
          endorsement:    f.endorsement    || null,
          experienceType: f.experienceType || null,
          zip:            f.zip            || null,
          radius:         (f.zip || this.geoLat()) && f.radius !== 50 ? f.radius : null,
          lat:            this.geoLat()    || null,
          lng:            this.geoLng()    || null,
          locationLabel:  this.locationLabel() || null,
          paged:          this.currentPage() > 1 ? this.currentPage() : null,
          seed:           this.seed()      || null,
        },
        queryParamsHandling: 'replace',
        replaceUrl:          true,
      });
    }
  
    // ── Geolocation ────────────────────────────────────────────────────────
  
    useMyLocation(): void {
      if (!navigator.geolocation) return;
  
      this.geoLocating.set(true);
      this.geoSet.set(false);
  
      navigator.geolocation.getCurrentPosition(
        pos => {
          this.geoLat.set(pos.coords.latitude);
          this.geoLng.set(pos.coords.longitude);
          this.geoSet.set(true);
          this.geoLocating.set(false);
          this.filterForm.patchValue({ zip: '' });
          this.locationLabel.set('');
  
          const radius = this.filterForm.get('radius')!.value;
          if (!radius || radius === 10) {
            this.filterForm.patchValue({ radius: 50 });
          }
        },
        () => {
          this.geoLocating.set(false);
        },
        { timeout: 10000, maximumAge: 300000 },
      );
    }
  
    // ── City/State geocoding ───────────────────────────────────────────────
  
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private mapboxLoaded = false;
  
    private loadMapboxScript(): void {
      if (this.mapboxLoaded) return;
      this.mapboxLoaded = true;
      // Mapbox JS not needed for geocoding API — just fetch calls
    }
  
    onLocationInput(value: string): void {
      this.locationLabel.set(value);
      this.geoLat.set(null);
      this.geoLng.set(null);
      this.locationStatus.set('idle');
      this.hideSuggestions.set(true);
  
      if (value.trim().length < 2) {
        this.locationSuggestions.set([]);
        return;
      }
  
      this.locationStatus.set('loading');
  
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetchSuggestions(value), 350);
    }
  
    private async fetchSuggestions(query: string): Promise<void> {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`
          + `?access_token=${environment.mapboxAccessToken}&types=place&country=US&limit=5`;
  
        const res  = await fetch(url);
        const data = await res.json();
  
        if (!data.features?.length) {
          this.locationSuggestions.set([]);
          this.locationStatus.set('error');
          this.hideSuggestions.set(true);
          return;
        }
  
        this.locationSuggestions.set(data.features.map((f: any) => ({
          id:        f.id,
          placeName: f.place_name,
          text:      f.text,
          context:   f.place_name.replace(f.text + ', ', ''),
          lat:       f.center[1],
          lng:       f.center[0],
        })));
  
        this.locationStatus.set('found');
        this.hideSuggestions.set(false);
      } catch {
        this.locationSuggestions.set([]);
        this.locationStatus.set('error');
        this.hideSuggestions.set(true);
      }
    }
  
    selectSuggestion(suggestion: LocationSuggestion): void {
      this.geoLat.set(suggestion.lat);
      this.geoLng.set(suggestion.lng);
      this.locationLabel.set(suggestion.placeName);
      this.locationSuggestions.set([]);
      this.hideSuggestions.set(true);
      this.locationStatus.set('found');
      this.filterForm.patchValue({ zip: '' });
      this.geoSet.set(true);
  
      const radius = this.filterForm.get('radius')!.value;
      if (!radius || radius === 10) {
        this.filterForm.patchValue({ radius: 50 });
      }
    }
  
    closeSuggestions(): void {
      // Small delay so click on suggestion registers before blur closes it
      setTimeout(() => this.hideSuggestions.set(true), 150);
    }
  
    toggleFilters(): void {
      this.filtersOpen.update(v => !v);
    }
  
    formatRadius(value: number): string {
      return `${value} mi`;
    }
  }
  